import { useCallback, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Form } from '@musinsa/pbo-core'
import PageHeader from '@/components/pbo/PageHeader'
import { notice } from '@/components/pbo/notice'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { PboScope } from '@/components/pbo/PboScope'
import { canSelectShipper, defaultShipper, scopedShipperCode, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useGetShipperOptions } from '@/services/inventory/stockLedger/useStockLedgerQueries'
import {
  DAILY_CLOSE_PRODUCT_HEADS_EXCEL_URL,
  DAILY_CLOSE_PRODUCT_ITEMS_EXCEL_URL,
} from '@/services/inventory/dailyCloseProduct/apis'
import { dailyCloseProductQueryKeys, useGetDailyCloseProductHeads, useGetDailyCloseProductItems } from '@/services/inventory/dailyCloseProduct/useDailyCloseProductQueries'
import type {
  DailyCloseProductDownloadReason,
  DailyCloseProductHead,
  DailyCloseProductItemSearch,
  DailyCloseProductSearch,
} from '@/services/inventory/dailyCloseProduct/types'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useEmptySearchNotice } from '../dailyCloseShared/useEmptySearchNotice'
import { DETAIL_DEFAULT_PAGE_SIZE, DETAIL_MAX_PAGE_SIZE, SCREEN_TITLE } from './constants'
import { DailyCloseProductFilter } from './components/DailyCloseProductFilter'
import { DailyCloseProductTables } from './components/DailyCloseProductTables'
import { ExcelDownloadReasonModal } from './components/ExcelDownloadReasonModal'
import type { DailyCloseProductFormValues } from './filterToRequest'
import { toHeadSearch, validateDailyCloseProductForm } from './filterToRequest'
import { DailyCloseProductSearchQuery, fromSearchQuery, hasDailyCloseProductSearchQuery, toSearchQuery } from './searchQuery'

type ExcelTarget = 'head' | 'item'
interface HeadView {
  search: DailyCloseProductSearch
  seq: number
}

function DailyCloseProduct() {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()
  const menuSession = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<DailyCloseProductSearchQuery>()
  const scope = useMemo(() => withCoMingle(shipperScopeOf(AuthStorage.user.get()), true), [])
  const defaults = useMemo<DailyCloseProductFormValues>(() => ({
    shipperCode: defaultShipper(scope).shipperCode || undefined,
    closeDateRange: [dayjs(), dayjs()],
  }), [scope])
  const [initialValues] = useState<DailyCloseProductFormValues>(() => {
    const restored = fromSearchQuery(parsedQs, defaults)
    if (scope.locked) restored.shipperCode = scopedShipperCode(scope, restored.shipperCode)
    return restored
  })
  const hasRestoredSearch = hasDailyCloseProductSearchQuery(parsedQs)
  const [headView, setHeadView] = useState<HeadView | null>(() => {
    if (!hasRestoredSearch || validateDailyCloseProductForm(initialValues)) return null
    return { search: toHeadSearch(initialValues), seq: 1 }
  })
  const seqRef = useRef(headView?.seq ?? 0)
  const [itemRequest, setItemRequest] = useState<DailyCloseProductItemSearch | null>(null)
  const [excelTarget, setExcelTarget] = useState<ExcelTarget | null>(null)

  const headsQuery = useGetDailyCloseProductHeads(headView?.search ?? null)
  const { refetch: refetchHeads } = headsQuery
  const markSearchForEmptyNotice = useEmptySearchNotice(headView?.seq, headsQuery)
  const itemsQuery = useGetDailyCloseProductItems(itemRequest)
  const { options: allShipperOptions } = useGetShipperOptions()
  const shipperOptions = useMemo(
    () => scope.locked
      ? selectableShippers(scope).map((shipper) => ({ value: shipper.shipperCode, label: shipper.shipperName || shipper.shipperCode }))
      : allShipperOptions,
    [allShipperOptions, scope],
  )
  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload({ fileNamePrefix: '일마감조회_상품별', config: { method: 'post' }, errorToast: true })

  const handleSearch = useCallback((rawValues: DailyCloseProductFormValues) => {
    const values = scope.locked ? { ...rawValues, shipperCode: scopedShipperCode(scope, rawValues.shipperCode) } : rawValues
    const message = validateDailyCloseProductForm(values)
    if (message) { notice.invalid(message); return }

    const search = toHeadSearch(values)
    // [조회] 는 늘 새로 받는다. 헤더 캐시는 탭 복귀용이라 스스로 다시 받지 않는다(useGetDailyCloseProductHeads).
    //  같은 조건은 refetch, 새 조건은 남은 헤더 캐시(A → B → A)를 지운 뒤 키를 바꿔 받는다.
    if (headView && JSON.stringify(headView.search) === JSON.stringify(search)) {
      refetchHeads().catch(() => undefined)
    } else {
      queryClient.removeQueries({ queryKey: dailyCloseProductQueryKeys.headsAll })
    }
    seqRef.current += 1
    markSearchForEmptyNotice(seqRef.current)
    setHeadView({ search, seq: seqRef.current })
    setItemRequest(null)
    const query = toSearchQuery(values)
    MenuStorage.menuOnSession.setSearch(query)
    replaceQuery(query)
    menuSession.reload()
  }, [headView, markSearchForEmptyNotice, menuSession, queryClient, refetchHeads, replaceQuery, scope])

  const handleReset = useCallback(() => {
    const today = dayjs()
    form.setFieldsValue({
      ...defaults,
      closeDateRange: [today, today],
      brandName: undefined,
      goodsCode: undefined,
      barcodes: undefined,
      logisticsServiceType: undefined,
    })
    MenuStorage.menuOnSession.setSearch({})
    replaceQuery({})
    menuSession.reload()
  }, [defaults, form, menuSession, replaceQuery])

  const handleHeadDoubleClick = useCallback((row: DailyCloseProductHead) => {
    if (!headView || !row.closeDate || !row.warehouseName) return
    setItemRequest({
      ...headView.search,
      shipperCode: row.shipperCode,
      closeDateFrom: row.closeDate,
      closeDateTo: row.closeDate,
      warehouseName: row.warehouseName,
      page: 1,
      pageSize: DETAIL_DEFAULT_PAGE_SIZE,
    })
  }, [headView])

  const handleItemPage = useCallback((page: number, pageSize: number) => {
    setItemRequest((current) => {
      if (!current) return current
      const nextPageSize = Math.min(pageSize, DETAIL_MAX_PAGE_SIZE)
      return { ...current, page: nextPageSize === current.pageSize ? page : 1, pageSize: nextPageSize }
    })
  }, [])

  const handleExcelConfirm = useCallback((reason: DailyCloseProductDownloadReason) => {
    const target = excelTarget
    setExcelTarget(null)
    if (target === 'head' && headView) {
      onDownloadExcel(DAILY_CLOSE_PRODUCT_HEADS_EXCEL_URL, { ...headView.search, ...reason }).catch(() => undefined)
    } else if (target === 'item' && itemRequest) {
      onDownloadExcel(DAILY_CLOSE_PRODUCT_ITEMS_EXCEL_URL, { ...itemRequest, ...reason }).catch(() => undefined)
    }
  }, [excelTarget, headView, itemRequest, onDownloadExcel])

  const selectedHead = useMemo(
    () => itemRequest && { shipperCode: itemRequest.shipperCode, closeDate: itemRequest.closeDateFrom, warehouseName: itemRequest.warehouseName },
    [itemRequest],
  )

  const itemPageInfo = itemsQuery.data?.pageInfo ?? {
    page: itemRequest?.page ?? 1,
    size: itemRequest?.pageSize ?? DETAIL_DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  }

  return (
    <PboScope>
      <PageHeader title={SCREEN_TITLE} />
      <DailyCloseProductFilter
        form={form}
        initialValues={initialValues}
        shipperOptions={shipperOptions}
        canEditShipper={canSelectShipper(scope)}
        clearsShipperOnServiceType={!scope.locked}
        onSearch={handleSearch}
        onReset={handleReset}
      />
      <DailyCloseProductTables
        heads={headsQuery.data ?? []}
        items={itemsQuery.data?.items ?? []}
        itemPageInfo={itemPageInfo}
        selectedHead={selectedHead}
        isHeadLoading={headsQuery.isFetching}
        isItemLoading={itemsQuery.isFetching}
        isDownloading={isDownloading}
        onHeadDoubleClick={handleHeadDoubleClick}
        onItemPage={handleItemPage}
        onDownload={setExcelTarget}
      />
      <ExcelDownloadReasonModal open={excelTarget != null} onConfirm={handleExcelConfirm} onCancel={() => setExcelTarget(null)} />
    </PboScope>
  )
}

export default DailyCloseProduct
